import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";

import { FeatureSessionStore } from "shared/src/uidispatcherapi/FeatureSessionStore";
import CartActions from "../actions/CartActions";
import { Store } from "shared/src/stores/Store";
import { AnonymousCartStore } from "./AnonymousCartStore";
import { CustomerCartStore } from "./CustomerCartStore";
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { BundleWithQuantity } from "../utils/ListBulkSelectionUtils";
import { isTier12Customer } from "shared/src/stores/SessionStore";
import { CustomerCartItemPrices } from "shared/src/types/CartPrice";

type BettyBundleId = string;

interface Backend {
    handleSetItemInCart(bettyBundleId: BettyBundleId, amount: number): void;
    handleChangeAmountInCart(bettyBundleId: BettyBundleId, amount: number): void;
    handleBulkAddToBasket(bundlesWithQuantity: BundleWithQuantity[]): void;
    getItemCount(): number;
    getItemAmount(bettyBundleId: BettyBundleId): number | null;
    getCartItemTotalNetPrice(bettyBundleId: BettyBundleId): ShoppingCartPosition | null;
    isEmpty(): boolean;
    getAllCartItems(): any[];
    getCartCurrentDnrNumbers(): string[];
    getPricesWithDnrInCart(): CustomerCartItemPrices[] | null;
}

// The CartStore abstracts over the CustomerCart and AnonymousCart.
export class CartStoreImpl extends Store implements Backend {
    activeBackend: Backend | null = null;

    constructor() {
        super();
        this.listenTo(CartActions.setItemInCart, this.handleSetItemInCart as any);
        this.listenTo(CartActions.changeAmountInCart, this.handleChangeAmountInCart as any);
        this.listenTo(CartActions.handleBulkAddToBasket, this.handleBulkAddToBasket as any);
        this.listenTo(AnonymousCartStore, this.handleBackendUpdate.bind(this, AnonymousCartStore));
        this.listenTo(CustomerCartStore, this.handleBackendUpdate.bind(this, CustomerCartStore));
        this.listenTo(SessionStore, this.handleSessionStoreUpdate);
        this.listenTo(FeatureSessionStore, this.handleSessionStoreUpdate);

        this.activeBackend = CartStoreImpl._getBackend();
    }

    static _getBackend(): Backend | null {
        if (SessionStore.loggedIn() && !isTier12Customer()) {
            return CustomerCartStore;
        } else if ((SessionStore.notLoggedIn() || isTier12Customer()) && AnonymousCartStore.isEnabled()) {
            return AnonymousCartStore;
        } else {
            return null;
        }
    }

    handleSessionStoreUpdate() {
        const newBackend = CartStoreImpl._getBackend();

        if (this.activeBackend !== newBackend) {
            this.activeBackend = newBackend;
            this.trigger();
        }
    }

    handleBackendUpdate(backend: Backend) {
        if (this.activeBackend === backend) {
            this.trigger();
        }
    }

    handleSetItemInCart(bettyBundleId: BettyBundleId, amount: number) {
        if (this.activeBackend) {
            this.activeBackend.handleSetItemInCart(bettyBundleId, amount);
        }
    }

    handleChangeAmountInCart(bettyBundleId: BettyBundleId, amount: number) {
        if (this.activeBackend) {
            this.activeBackend.handleChangeAmountInCart(bettyBundleId, amount);
        }
    }

    handleBulkAddToBasket(bundlesWithQuantity: BundleWithQuantity[]) {
        if (this.activeBackend) {
            this.activeBackend.handleBulkAddToBasket(bundlesWithQuantity);
        }
    }

    getItemCount(): number {
        if (this.activeBackend) {
            return this.activeBackend.getItemCount();
        }
        return 0;
    }

    getAllCartItems(): any[] {
        if (this.activeBackend) {
            return this.activeBackend.getAllCartItems();
        }
        return [];
    }

    getItemAmount(bettyBundleId: BettyBundleId): number | null {
        if (this.activeBackend) {
            return this.activeBackend.getItemAmount(bettyBundleId);
        }
        return null;
    }

    getCartItemTotalNetPrice(bettyBundleId: BettyBundleId): ShoppingCartPosition | null {
        if (this.activeBackend) {
            return this.activeBackend.getCartItemTotalNetPrice(bettyBundleId);
        }
        return null;
    }

    isEmpty(): boolean {
        if (this.activeBackend) {
            return this.activeBackend.isEmpty();
        }
        return true;
    }

    isEnabled(): boolean {
        return this.activeBackend !== null;
    }

    getCartCurrentDnrNumbers(): string[] {
        if (this.activeBackend) {
            return this.activeBackend.getCartCurrentDnrNumbers();
        }
        return [];
    }

    getPricesWithDnrInCart(): CustomerCartItemPrices[] | null {
        if (this.activeBackend) {
            return this.activeBackend.getPricesWithDnrInCart();
        }
        return null;
    }
}

export const CartStore = new CartStoreImpl();
